import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Check, AlertCircle, Loader2, Save, X, ListChecks, Download, Info, Trophy } from 'lucide-react';
import { CommunityWatchlistRowWithModal } from '@/components/CommunityWatchlistRow';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import ImportFromPrivateWatchlistModal from '@/components/ImportFromPrivateWatchlistModal';
import TickerSearchInput from '@/components/TickerSearchInput';
import RefreshNowButton from '@/components/RefreshNowButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Community Watchlists tab. Lets the user manage up to 3 public
 * watchlists × 5 tickers each (15 tickers max published).
 *
 * Privacy gate: backend rejects publish unless `share_tickers` is
 * enabled in privacy settings. We surface that error inline and
 * link to the Settings tab.
 */
export default function CommunityWatchlistsTab({ me }) {
  const [lists, setLists] = useState([]);
  const [meta, setMeta] = useState({ max_lists: 3, max_tickers_per_list: 5 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {watchlist_id|null, name, tickers}
  const [importOpen, setImportOpen] = useState(false);
  const sharingOn = !!me?.privacy?.share_tickers;

  // Round 84c — tickers already used in this user's OTHER community
  // watchlists. We compute it whenever `lists` or `editing` changes so
  // both the editor and the import modal know what to disable.
  const tickersInOtherLists = useMemo(() => {
    const out = new Set();
    for (const wl of lists) {
      if (editing && wl.watchlist_id === editing.watchlist_id) continue;
      for (const t of (wl.tickers || [])) out.add(String(t).toUpperCase());
    }
    return Array.from(out);
  }, [lists, editing]);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/community/watchlists/mine`, { withCredentials: true });
      setLists(data.watchlists || []);
      setMeta({ max_lists: data.max_lists, max_tickers_per_list: data.max_tickers_per_list });
    } catch {
      toast.error('Could not load your watchlists.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const startNew = () => {
    if (lists.length >= meta.max_lists) {
      toast.error(`You can publish at most ${meta.max_lists} watchlists.`, { description: 'Delete one to free a slot.' });
      return;
    }
    setEditing({ watchlist_id: null, name: '', tickers: [] });
  };

  const startEdit = (wl) => {
    setEditing({ watchlist_id: wl.watchlist_id, name: wl.name, tickers: [...(wl.tickers || [])] });
  };

  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    const tickers = (editing.tickers || []).filter(Boolean);
    if (!editing.name?.trim()) return toast.error('Name is required.');
    if (tickers.length === 0) return toast.error('Add at least one ticker.');
    if (tickers.length > meta.max_tickers_per_list) {
      return toast.error(`At most ${meta.max_tickers_per_list} tickers per watchlist.`);
    }
    try {
      if (editing.watchlist_id) {
        await axios.patch(
          `${API}/community/watchlists/${editing.watchlist_id}`,
          { name: editing.name.trim(), tickers },
          { withCredentials: true },
        );
        toast.success('Watchlist updated.');
      } else {
        await axios.post(
          `${API}/community/watchlists`,
          { name: editing.name.trim(), tickers },
          { withCredentials: true },
        );
        toast.success('Watchlist published!');
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    }
  };

  const deleteList = async (wl) => {
    if (!window.confirm(`Delete "${wl.name}"?`)) return;
    try {
      await axios.delete(`${API}/community/watchlists/${wl.watchlist_id}`, { withCredentials: true });
      toast.success('Deleted.');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid="community-watchlists-tab">
      {!sharingOn && (
        <div className="rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-950/30 p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs sm:text-sm flex-1">
            <strong>Public sharing is OFF.</strong> Enable "Share Ticker Symbols" in the Settings tab to publish your watchlists.
          </div>
        </div>
      )}

      {/* Round 84c — uniqueness rule banner */}
      <div
        className="rounded-lg border border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-950/30 p-3 flex items-start gap-2"
        data-testid="community-watchlists-rules-banner"
      >
        <Info className="w-4 h-4 text-violet-600 dark:text-violet-400 shrink-0 mt-0.5" />
        <div className="text-[11px] sm:text-xs flex-1 leading-relaxed">
          <strong>Rules:</strong> up to <strong>{meta.max_lists} watchlists</strong> × <strong>{meta.max_tickers_per_list} tickers</strong> each.
          {' '}Each ticker can appear in only <strong>one</strong> of your community lists — keeps your three lists distinct from each other (no near-duplicates).
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-muted-foreground">
          {lists.length} of {meta.max_lists} watchlists used · {meta.max_tickers_per_list} tickers max per list
        </p>
        <div className="flex items-center gap-2">
          {lists.length > 0 && (
            <RefreshNowButton
              scope="watchlists"
              label="Refresh prices"
              onRefreshed={refresh}
            />
          )}
          <Button
            onClick={startNew}
            disabled={lists.length >= meta.max_lists || !sharingOn}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="community-watchlist-new-btn"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1" /> New Watchlist
          </Button>
        </div>
      </div>

      {editing && (
        <div className="rounded-2xl border-2 border-violet-300 dark:border-violet-700 bg-card p-4" data-testid="community-watchlist-editor">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-foreground flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-violet-500 dark:text-violet-400" />
              {editing.watchlist_id ? 'Edit Watchlist' : 'New Watchlist'}
            </h3>
            <button onClick={cancelEdit} className="text-muted-foreground hover:text-foreground" data-testid="community-watchlist-editor-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1">Name (max 60 chars)</label>
              <input
                type="text"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                maxLength={60}
                placeholder="e.g. Monthly Income Picks"
                className="w-full h-10 rounded-lg border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="community-watchlist-name-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-muted-foreground">Tickers (max {meta.max_tickers_per_list})</label>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300"
                onClick={() => setImportOpen(true)}
                data-testid="community-watchlist-import-btn"
              >
                <Download className="w-3 h-3" />
                Import from my Watchlist
              </button>
            </div>
            <TickerEditor
              tickers={editing.tickers}
              max={meta.max_tickers_per_list}
              onChange={(arr) => setEditing({ ...editing, tickers: arr })}
              blockedTickers={tickersInOtherLists}
            />
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="ghost" onClick={cancelEdit} data-testid="community-watchlist-cancel-btn">Cancel</Button>
              <Button
                onClick={saveEdit}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="community-watchlist-save-btn"
              >
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {lists.length === 0 && !editing && (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
            You haven't published any watchlists yet. Click "New Watchlist" to start.
          </div>
        )}
        {lists.map((wl) => (
          <CommunityWatchlistRowWithModal
            key={wl.watchlist_id}
            wl={wl}
            onEdit={startEdit}
            onDelete={deleteList}
          />
        ))}
      </div>

      <ImportFromPrivateWatchlistModal
        open={importOpen && !!editing}
        onClose={() => setImportOpen(false)}
        maxSelectable={meta.max_tickers_per_list}
        alreadyInThisList={editing?.tickers || []}
        usedAcrossOtherLists={tickersInOtherLists}
        onImport={(picked) => {
          // Merge with whatever's already in the editor (dedup, cap)
          const current = (editing?.tickers || []).map((t) => t.toUpperCase());
          const merged = [...current];
          for (const t of picked) {
            const u = String(t).toUpperCase();
            if (!merged.includes(u) && merged.length < meta.max_tickers_per_list) {
              merged.push(u);
            }
          }
          setEditing({ ...(editing || {}), tickers: merged });
          toast.success(`Imported ${picked.length} ticker${picked.length === 1 ? '' : 's'}.`);
        }}
      />
    </div>
  );
}

function TickerEditor({ tickers, max, onChange, blockedTickers = [] }) {
  const blockedSet = new Set((blockedTickers || []).map((t) => String(t).toUpperCase()));
  const add = (t) => {
    const u = String(t || '').trim().toUpperCase();
    if (!u) return;
    if (tickers.includes(u)) return;
    if (blockedSet.has(u)) {
      toast.error(`${u} is already in another of your community lists.`, {
        description: 'Each ticker may only appear in one community watchlist.',
      });
      return;
    }
    if (tickers.length >= max) {
      toast.error(`Max ${max} tickers per list.`);
      return;
    }
    onChange([...tickers, u]);
  };
  const remove = (t) => onChange(tickers.filter((x) => x !== t));

  // Tickers we don't want to show in the dropdown as selectable:
  // already in another community list + already in THIS list.
  const dropdownBlocked = [
    ...Array.from(blockedSet),
    ...tickers.map((t) => t.toUpperCase()),
  ];

  return (
    <div>
      <TickerSearchInput
        onSelect={add}
        placeholder="Search ticker (e.g. SCHD)"
        blockedTickers={dropdownBlocked}
        blockedHint="already chosen"
        testid="watchlist-ticker"
      />
      <div className="flex flex-wrap gap-1.5 mt-2">
        {tickers.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-violet-100 dark:bg-violet-950/60 text-violet-800 dark:text-violet-300 font-mono"
            data-testid={`community-watchlist-ticker-chip-${t}`}
          >
            {t}
            <button type="button" onClick={() => remove(t)} className="hover:text-rose-500" aria-label={`Remove ${t}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
